import React, { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import "../css/dashboard-charts.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const GraphData = ({ graphState, onDurationChange, durationType }) => {
  
  const getArrangedData = useMemo(() => {
    if (!graphState || !graphState.labels) return { tenant: [], landlord: [], property: [] };
    
    let tenant = [];
    let landlord = [];
    let property = [];
    for (let day of graphState.labels) {
      tenant.push(graphState.tenantData?.[day] || 0);
      landlord.push(graphState.landlordData?.[day] || 0);
      property.push(graphState.propertyData?.[day] || 0);
    }
    return { tenant, landlord, property };
  }, [graphState]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll use custom legends or the default one with better styling
      },
      tooltip: {
        backgroundColor: '#1e293b',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        cornerRadius: 8,
        displayColors: true,
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { 
          color: '#94a3b8', 
          font: { size: 11, weight: '600' },
          callback: function(val, index) {
            const label = this.getLabelForValue(val);
            return label.replace('Week ', 'W');
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f1f5f9' },
        ticks: { color: '#94a3b8', font: { size: 11, weight: '600' } }
      }
    }
  };

  const registrationBarData = {
    labels: graphState?.labels || [],
    datasets: [
      {
        label: "Tenants",
        data: getArrangedData.tenant,
        backgroundColor: '#a3eb65',
        borderRadius: 4,
      },
      {
        label: "Landlords",
        data: getArrangedData.landlord,
        backgroundColor: '#253837',
        borderRadius: 4,
      }
    ]
  };

  const barChartData = {
    labels: graphState?.labels || [],
    datasets: [
      {
        label: "Properties",
        data: getArrangedData.property,
        backgroundColor: '#a3eb65',
        borderRadius: 0,
        barThickness: 20,
      }
    ]
  };

  const doughnutData = {
    labels: ["Tenants", "Landlords"],
    datasets: [
      {
        data: [graphState?.totalTenantCount || 0, graphState?.totalLandlordCount || 0],
        backgroundColor: ['#a3eb65', '#253837'],
        borderWidth: 0,
        hoverOffset: 10,
      }
    ]
  };

  const handleToggle = (type) => {
    onDurationChange(type);
  };

  return (
    <div className="dashboard-charts-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 style={{ fontWeight: '800', color: '#1e293b', margin: 0, fontSize: '1.25rem' }}>Growth Analytics</h4>
          <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '4px 0 0 0' }}>Visualizing registrations and activity trends</p>
        </div>
        <div className="duration-toggle-group">
          {['daily', 'weekly', 'monthly'].map((type) => (
            <button
              key={type}
              onClick={() => handleToggle(type)}
              className={`duration-btn ${durationType === type ? 'active' : ''}`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <Row className="g-4">
        {/* Registration Trend Chart */}
        <Col lg={8} md={12}>
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h6 className="chart-title">Registration Trend</h6>
                <p className="chart-subtitle">New user signups over time</p>
              </div>
              <div className="d-flex gap-3">
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: '#a3eb65' }}></div>
                  <span>Tenants</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: '#253837' }}></div>
                  <span>Landlords</span>
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ height: '350px' }}>
              <Bar 
                data={registrationBarData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    datalabels: { display: false }
                  }
                }} 
              />
            </div>            
          </div>
        </Col>

        {/* User Distribution Chart */}
        <Col lg={4} md={12}>
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h6 className="chart-title">User Distribution</h6>
                <p className="chart-subtitle">Total platform composition</p>
              </div>
            </div>
            <div className="doughnut-container">
              <Doughnut 
                data={doughnutData} 
                options={{
                  ...chartOptions,
                  cutout: '65%',
                  plugins: {
                    ...chartOptions.plugins,
                    legend: {
                      display: true,
                      position: 'bottom',
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: { size: 12, weight: '600' }
                      }
                    },
                    datalabels: {
                      display: true,
                      color: '#fff',
                      padding: 6,
                      font: {
                        weight: 'bold',
                        size: 14
                      },
                      formatter: (value) => {
                        return value > 0 ? value : '';
                      }
                    }
                  },
                  scales: { x: { display: false }, y: { display: false } }
                }} 
              />
            </div>
          </div>
        </Col>

        {/* Property Uploads Chart */}
        <Col lg={12}>
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h6 className="chart-title">Property Upload Activity</h6>
                <p className="chart-subtitle">Inventory growth tracking</p>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#a3eb65' }}></div>
                <span>New Properties</span>
              </div>
            </div>
            <div className="chart-container" style={{ height: '250px' }}>
              <Bar data={barChartData} options={chartOptions} />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default GraphData;
